/**
 * The product profile the running app reads: which modules this product offers, where it
 * lands, and what its sign-in page says. The build-time half — title, icons, default mode —
 * is `products.ts`, which `vite.config.ts` also imports.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT A PROFILE MAY DECIDE, AND WHAT IT MAY NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It decides what EXISTS in this product: a hidden tab is removed from the rail and bounced
 * from the URL through the same `hiddenItems` path a missing permission already uses, so a
 * product can never reveal a screen a role is refused — it can only hide one.
 *
 * It does NOT decide what the console SAYS. Verdict colours, severity, thresholds and the
 * alarm vocabulary are the same in every product; a white-label that re-hued `--danger` to
 * match a logo would change the meaning of the screen, not its look. That is why Cratus's red
 * lives in its artwork and its sign-in page, and the organisation's accent stays the
 * organisation's (Organisation → Appearance). See docs/CRATUS.md.
 */
import { resolveProductId, type ProductId } from './products';

export interface SignInFeature {
  /** The product area it belongs to — printed as a mono tag above the heading. */
  module: string;
  k: string;
  v: string;
}

export interface SignInCopy {
  eyebrow: string;
  /** Two lines. A product's stylesheet block may colour the second (signin.css; Cratus does). */
  headline: readonly [string, string];
  lede: string;
  /** Only what ships. The Thunder list's own rule: none describes anything that does not. */
  features: readonly SignInFeature[];
  /** Real counts only, and an empty list renders nothing — never a round number to fill it. */
  scope: readonly { n: string; l: string }[];
  legal: { terms: string; privacy: string; support: string };
  /** The platform line under the form, or null for the product that IS the platform. */
  poweredBy: string | null;
}

export interface ProductProfile {
  id: ProductId;
  /** The product's name as a person reads it. */
  name: string;
  /**
   * Tab ids this product does not offer. Joined into `hiddenItems` in `App.tsx`, so the rail
   * drops them and a bookmarked URL is sent to `homeTab`.
   */
  hiddenTabs: ReadonlySet<string>;
  /**
   * Whether this product offers the quick-links rail beside the content.
   *
   * A product decision rather than a token, because it is about what the console OFFERS:
   * Thunder's shell is a left rail and a full-width content pane, and adding a second rail
   * to it would be a layout change nobody asked for. Cratus takes it because its design
   * language is built around one.
   */
  quickLinks: boolean;
  /**
   * Whether this product shows the assistant composer below the content.
   *
   * There is no assistant behind it in either product yet — see `AiDock`. The flag exists
   * because the dock is part of the Cratus design language's shell and the layout is built
   * around it, while Thunder's shell has no such row and should not grow one.
   */
  aiDock: boolean;
  /**
   * Whether this product navigates by the left rail.
   *
   * Cratus does not: its design language navigates from the Home screen's module tiles, the
   * breadcrumb, the quick-links rail and the search palette, and the owner chose that layout over
   * a restyled rail (2026-10-15). Thunder keeps its rail. Either way the rail's filtering — hidden
   * tabs, `needs`, the remote-control gate — is the same `hiddenItems` list, so the Home screen and
   * the rail can never offer different screens.
   */
  sidebar: boolean;
  /** Where the product opens, and where a hidden or unknown tab falls back to. */
  homeTab: 'overview' | 'command' | 'home';
  /**
   * A permission a tab needs IN THIS PRODUCT, in place of the rail's own (`NavItem.needs`). Like
   * `hiddenTabs`, it can only narrow who is offered a screen, never widen it: it is checked through
   * the same `hiddenItems` path, and the API still decides what any request may read.
   */
  tabNeeds: Readonly<Record<string, string>>;
  /** `null` means the sign-in copy compiled into `LumenSignIn` — Thunder's own. */
  signIn: SignInCopy | null;
  /**
   * The prefix of what this product keeps in the browser — `<prefix>.organisation`. Each product
   * has its own, because two products served from one origin (every local stack) otherwise share
   * one selection: an organisation chosen in the Thunder console opened the Cratus console there,
   * and a Thunder sub-organisation with no sites looked like a Cratus console with no data.
   */
  storagePrefix: string;
  /**
   * The organisation this product is FOR, by slug. A person who reaches it from above — a
   * platform administrator — opens the product there rather than in their own home organisation,
   * until they choose another. Null: the home organisation, as before.
   */
  organisationSlug: string | null;
  /**
   * Whether Complete asks what the work found before it is offered (CRATUS 2.0 #9: no alarm
   * closes without a cause). The API takes a cause from every product; this is whether the
   * console insists on one.
   */
  requireRootCause: boolean;
  /**
   * Whether the product draws the POWER CHAIN — single-line diagrams, plants and the twin. When
   * false the console does not ask for a site's topology at all: every site would answer
   * `topology-not-drawn`, once per site per session, for a picture the product never shows.
   */
  drawsPowerChain: boolean;
  /**
   * The site hub's sub-tabs this product does not offer, the one a site opens on, and what a site
   * is MADE OF here. `power-chain`: plants, meters and rectifiers, so Analytics reads production
   * and supply and Devices files equipment into plants. `cabinets`: the cabinets on a tower and
   * their controllers, so Analytics reads the cabinets, Alarms is the work queue for the site and
   * Devices says which cabinet each controller serves.
   */
  siteHub: { hiddenTabs: ReadonlySet<string>; homeTab: string; subject: 'power-chain' | 'cabinets' };
  /**
   * HOW THIS PRODUCT TALKS ABOUT ACCESS. The permission catalogue is the platform's, served whole
   * (Lumen-BE 0042), and its copy describes Thunder's power plant: "Change what a plant IS — its
   * compliance parameters". This is display only — what a role holds and what the API enforces
   * are untouched — so a hidden permission still counts on the server, and is simply not
   * offered here as a right worth explaining.
   */
  /** The examples Integrations offers, in this product's feeds and names. */
  integrations: { feedsDesc: string; topic: string; source: string; label: string };
  access: {
    /** Permissions whose screens the product does not offer, left out of every grid and matrix. */
    hiddenPermissions: ReadonlySet<string>;
    /** Copy that replaces the server's for this product, by slug. */
    permissionCopy: Readonly<Record<string, { label?: string; blurb?: string }>>;
    /** Area headings, by the server's area name. */
    areaNames: Readonly<Record<string, string>>;
    /** What each system role is FOR, in this product's words. */
    roleBlurbs: Readonly<Record<string, string>>;
  };
}

const THUNDER: ProductProfile = {
  id: 'thunder',
  name: 'Thunder',
  /* The Command Centre is Cratus's landing, and cabinets are Cratus's unit of onboarding.
     Thunder keeps Overview and its site wizard, so its rail is unchanged. Analysis reads a
     Cratus controller's parts; Thunder's own series explorer is Telemetry. */
  hiddenTabs: new Set(['home', 'profiles', 'design', 'site-center', 'appearance', 'command', 'cabinets', 'cabinet', 'cabinet-onboarding', 'analysis', 'work', 'fleet-reports', 'assets']),
  homeTab: 'overview',
  quickLinks: false,
  aiDock: false,
  sidebar: true,
  tabNeeds: {},
  signIn: null,
  /* Unchanged, so a Thunder console keeps the selection every browser already holds. */
  storagePrefix: 'lumen',
  organisationSlug: null,
  requireRootCause: false,
  drawsPowerChain: true,
  /* `overview` is the Cratus site's glance page; a power plant's equivalent is `sld`'s three pictures. */
  siteHub: { hiddenTabs: new Set(['cabinets', 'overview']), homeTab: 'sld', subject: 'power-chain' },
  integrations: {
    feedsDesc: 'Which publishing feeds serve which site. Many gateways may feed one site — that is the normal shape of a large plant, not an exception.',
    topic: 'site_events_jazz',
    source: 'JAZZMSC5-HALL-A',
    label: 'Row A gateway',
  },
  access: {
    /* The cabinet form's right, for a screen Thunder does not offer. */
    /* Work orders (0092) are Cratus's screen; their two rights would be boxes for a screen Thunder never shows. */
    hiddenPermissions: new Set(['cabinets.form.edit', 'work.manage', 'work.update']),
    permissionCopy: {},
    areaNames: {},
    roleBlurbs: {
      admin: 'Everything an engineer can do, plus inviting people and changing their access.',
      engineer: 'Edits sites, devices, diagrams and parameters. Cannot manage people.',
      auditor: 'Reads the audit trail and manages findings. Cannot change what produced them.',
      operator: 'Reads the plant and works alarms. Changes nothing.',
      sales: 'Read-only across the console.',
    },
  },
};

const CRATUS: ProductProfile = {
  id: 'cratus',
  name: 'Cratus',
  /*
   * WHAT CRATUS DOES NOT OFFER, as a first cut to be confirmed with the client (docs/CRATUS.md
   * §4). Their brief was specific capabilities rather than whole Thunder modules, and every one
   * of these is either standards compliance — which Cratus did not ask for — or power-chain
   * engineering, which is not the job of a cabinet-security console:
   *
   *   overview      replaced by the Command Centre, which leads with security rather than yield
   *   findings      compliance gaps
   *   twin          the power chain's isometric twin
   *   topology      SLD Studio
   *   calculators   power-engineering tools
   *   compliance, standards, reports   the Comply group
   *   onboarding    the 60-parameter site wizard. A Cratus site is created with its first
   *                 cabinet, on Cabinet onboarding, which asks only what a technician knows
   *
   * Remote control stays: re-securing a lock from the console is a Cratus use case, and the
   * organisation switch (ADR-0014) still decides whether it appears at all.
   */
  hiddenTabs: new Set([
    'overview',
    'onboarding',
    'findings',
    'twin',
    'topology',
    'calculators',
    'compliance',
    'standards',
    'reports',
    /* Profile Management is this product's Access: the same screen in the FESCO design's columns
       and dialogs (the owner, 2026-10-16). Two doors to one set of rules would be one too many. */
    'access',
  ]),
  homeTab: 'home',
  quickLinks: true,
  aiDock: true,
  sidebar: false,
  /*
   * TELEMETRY IS AN ENGINEER'S TOOL HERE. It is the raw series explorer — devices, parameters,
   * buckets, a cost guard — and a Cratus operator's questions are answered by the Command Centre,
   * the cabinet view and Analysis. Kept for whoever configures the catalogue (decided 2026-10-05,
   * CRATUS-ROADMAP.md §4.5), rather than hidden from everybody.
   */
  tabNeeds: { telemetry: 'metrics.edit' },
  storagePrefix: 'cratus',
  /* The Cratus organisation — where a Thunder administrator lands when opening this console. */
  organisationSlug: 'cratus',
  requireRootCause: true,
  drawsPowerChain: false,
  /*
   * A Cratus site is a tower with cabinets on it. The hub's Diagrams (single-line, power flow,
   * twin), its Compliance parameters and its device comparison are the power chain's and the
   * Comply group's, which this product does not offer (§4 of docs/CRATUS.md); a site opens on its
   * cabinets, the unit Cratus is built around.
   */
  siteHub: { hiddenTabs: new Set(['sld', 'compliance-params', 'compare']), homeTab: 'overview', subject: 'cabinets' },
  /* A Cratus feed is a site's controllers publishing under its SiteID (payload rev 3). */
  integrations: {
    feedsDesc: 'Which publishing feeds serve which site. A tower is fed by the controllers in its cabinets, each publishing under the site’s SiteID.',
    topic: 'cratus/site_events',
    source: 'PSH-1234',
    label: 'Tower controllers',
  },
  access: {
    /*
     * Rights over screens Cratus does not offer — the Comply group, SLD Studio, Calculators and
     * the 60-parameter site wizard (`hiddenTabs` above). The four from migration 0100
     * (`compliance.read`, `findings.read`, `calculators.use`, `sites.create`) join them for the
     * same reason: they exist so a Thunder organisation can HIDE a section, and a Cratus
     * administrator offered "View compliance" would be ticking a box for a tab that is not there.
     *
     * DISPLAY ONLY, as this whole block is. The server still holds the grants and still enforces
     * them, which is what keeps one database honest for both products; a Cratus console simply
     * does not present them as rights worth explaining. `sites.create` in particular takes
     * nothing away here — a Cratus site is created with its first cabinet, through
     * `POST /cabinets`, which is gated on `sites.edit` and calls `createSite` directly.
     */
    hiddenPermissions: new Set([
      'findings.manage',
      'topology.edit',
      'topology.delete',
      'reports.issue',
      'reports.retract',
      'compliance.read',
      'findings.read',
      'calculators.use',
      'sites.create',
    ]),
    permissionCopy: {
      'sites.edit': {
        label: 'Edit sites and cabinets',
        blurb: 'Onboard a cabinet, and change where a site is, what kind of site it is and the cabinets on it.',
      },
      'devices.integrate': {
        label: 'Integrate controllers',
        blurb: 'Admit a cabinet controller or other device discovered at a site, block it, or retire it. Nothing it sends is stored until it is admitted.',
      },
      'metrics.edit': {
        blurb: 'Rename a reading, set its unit and its words, and declare a value as "not a reading". Also opens Telemetry, the raw series explorer.',
      },
      'alarms.triage': {
        blurb: 'Take, acknowledge, note and complete alarms, and record what the investigation found. Separate from editing sites: working an alarm is not changing what the site is.',
      },
    },
    areaNames: { 'The plant': 'Sites & cabinets' },
    roleBlurbs: {
      admin: 'Everything an engineer can do, plus inviting people and changing their access.',
      engineer: 'Onboards sites and cabinets, integrates controllers and shapes the cabinet form. Cannot manage people.',
      auditor: 'Reads the audit trail. Cannot change what produced it.',
      operator: 'Watches the fleet and works alarms. Changes nothing else.',
      sales: 'Read-only across the console.',
    },
  },
  signIn: {
    eyebrow: 'Multi-layered protection',
    headline: ['Smart security suite', 'for critical infrastructure.'],
    lede:
      'Cratus watches the cabinets and sites you protect — doors, locks, power and environment — ' +
      'and says which one needs a person first.',
    /*
     * Five, like Thunder's, and each names something that ships in this build: the Command
     * Centre, the alarm workflow, the cabinet's live readings, analysis over time and site-level
     * access. Nothing about cabinet lifecycle, warranty or dimensioning — those screens are
     * designed, not built, and a sign-in page is the last place to promise them.
     *
     * NOT REMOTE CONTROL, which this list named until 2026-10-05 as "two-person approved". Both
     * halves were untrue for Cratus: a Cratus controller receives no command at all yet (payload
     * rev 3 is uplink only, and no downlink exists — CRATUS-ROADMAP.md §4.9), and a write inside
     * its cited safe band is approved by its proposer alone. It returns when lock and hooter
     * commands ship, saying exactly what approves them.
     */
    features: [
      {
        module: 'Command Centre',
        k: 'One screen for the whole fleet',
        v: 'Security, power and environment alarms ranked worst first, and every site that has stopped reporting — the site that needs a person, named.',
      },
      {
        module: 'Alarms',
        k: 'Every alarm has an owner',
        v: 'An alarm is an interval, not a flood of samples. It is assigned, acknowledged and closed by a named person, and the trail of who did what is kept.',
      },
      {
        module: 'Monitor',
        k: 'Live from the cabinet',
        v: 'Doors, locks, shocks, smoke, water, temperature and the cooling read live from each cabinet’s controller, with the age of every reading shown.',
      },
      {
        module: 'Analysis',
        k: 'What happened, and when',
        v: 'A few cabinets over a day, a week or a month on one chart: how hot against the cooling’s own set point, and every door opening and alarm on the same timeline.',
      },
      {
        module: 'Profile Management',
        k: 'Access down to the site',
        v: 'People see the organisations, sites and devices they are granted — and a screen their role cannot use is not offered to them.',
      },
    ],
    /* No scope strip until there are real Cratus counts worth printing on a login page. */
    scope: [],
    /*
     * Thunder operates the platform Cratus runs on, so its terms, privacy policy and support
     * desk apply until Cratus supplies its own — flagged in docs/CRATUS.md as a decision.
     */
    legal: {
      terms: 'https://thunderenergy.ai/legal/terms',
      privacy: '/legal/privacy',
      support: 'mailto:operations@thunderenergy.ai?subject=Cratus%20sign-in%20help',
    },
    poweredBy: 'Powered by Thunder Energy',
  },
};

/** The permission a rail entry needs in this product: the product's own, else the rail's. */
export function needsOf(profile: Pick<ProductProfile, 'tabNeeds'>, item: { id: string; needs?: string | undefined }): string | undefined {
  return profile.tabNeeds[item.id] ?? item.needs;
}

/** Every product's profile — exported for the tests that hold each one to what it ships. */
export const PROFILES: Record<ProductId, ProductProfile> = { thunder: THUNDER, cratus: CRATUS };

/**
 * The product this bundle was built as. Resolved once, at module load.
 *
 * The literal `import.meta.env.VITE_PRODUCT` expression is load-bearing: Vite substitutes it by
 * pattern-matching that exact text, as `FleetMap.tsx` notes for the map variables.
 */
export const PRODUCT: ProductProfile =
  PROFILES[resolveProductId(import.meta.env.VITE_PRODUCT as string | undefined)];
